// Chat attachments upload through our server (ChatUploadsController), like
// every other upload in the app. They used to go browser-direct to R2, which
// needs a CORS policy on the bucket, and never worked in production (#943).

export interface UploadResult {
  signedId: string;
  filename: string;
  contentType: string;
  byteSize: number;
}

// Carries the HTTP status (0 = the request never got a response) so a failure
// reported to Sentry says whether the server refused the file or the network
// dropped it.
export class ChatUploadError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ChatUploadError";
    this.status = status;
  }
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute("content") || "";
}

export async function uploadChatFile(file: File): Promise<UploadResult> {
  const formData = new FormData();
  formData.append("file", file);
  const headers = { "X-CSRF-Token": getCsrfToken() };

  let response: Response;
  try {
    response = await fetch("/chat/uploads", {
      method: "POST",
      credentials: "same-origin",
      headers,
      body: formData,
    });
  } catch (error) {
    throw new ChatUploadError(error instanceof Error ? error.message : "Network error", 0);
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ChatUploadError(body.errors?.join(", ") || `Chat upload failed (${response.status})`, response.status);
  }
  const body = await response.json();
  return {
    signedId: body.signed_id,
    filename: body.filename,
    contentType: body.content_type,
    byteSize: body.byte_size,
  };
}

// Extension by MIME subtype for the image types chat accepts.
const IMAGE_EXTENSIONS: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
};

// A pasted screenshot arrives as an anonymous "image.png"; give it a readable
// timestamped name for the conversation and downloads (#944). Returns null when
// the clipboard holds no image, so a plain text paste stays a text paste.
export function pastedImageFile(clipboard: { files: ArrayLike<File> }): File | null {
  const file = Array.from(clipboard.files).find((f) => f.type.startsWith("image/"));
  if (!file) return null;
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    pad(now.getHours()),
    pad(now.getMinutes()),
  ].join("-");
  const ext = IMAGE_EXTENSIONS[file.type] ?? file.type.split("/")[1];
  return new File([file], `pasted-image-${stamp}.${ext}`, { type: file.type });
}

export function inferKindFromContentType(contentType: string): "image" | "pdf" | "document" | "voice" {
  if (contentType.startsWith("image/")) return "image";
  if (contentType === "application/pdf") return "pdf";
  if (contentType.startsWith("audio/")) return "voice";
  return "document";
}

export const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

export const MAX_FILE_BYTES = 20 * 1024 * 1024;
